// Athlete-side result-entries ledger editor (Wave 2): swimming, gymnastics,
// rowing, golf, diving, beach_volleyball. Rendered by GameStatsPage for
// result_entries sports in place of the football position/year table. Add/edit/
// delete against /api/result-entries; mark_numeric + lower_is_better are
// computed client-side per sport (see lib/resultEntries.js). Beach volleyball is
// a flat ledger with one synthetic event ("pairs"); every other sport groups by
// the catalog event picklist.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useCatalog } from '../../context/CatalogContext';
import Button from '../Button';
import ConfirmModal from '../ConfirmModal';
import VerifiedFlame from '../VerifiedFlame';
import { lowerIsBetter, parseMarkNumeric, contextConfig, contextLabel } from '../../lib/resultEntries';
import { resolveGetConfig } from './editorConfig';
import './AthleteEditor.css';

const currentYear = () => new Date().getFullYear();
const EMPTY = { event_key: '', mark_value: '', meet_name: '', meet_date: '', season_year: '', context: {} };

export default function ResultEntriesEditor({ sportSlug, getConfig: getConfigProp }) {
  const { API_URL } = useAuth();
  const catalog = useCatalog();
  const isFlat = sportSlug === 'beach_volleyball';
  const contextFields = contextConfig(sportSlug).fields;

  const groupedPositions = catalog.groupedPositionsFor(sportSlug);
  const positions = catalog.positionsFor(sportSlug);
  const labelForEvent = useMemo(() => {
    const m = new Map(positions.map(p => [p.slug, p.label]));
    return (key) => m.get(key) || key;
  }, [positions]);

  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ ...EMPTY, event_key: isFlat ? 'pairs' : '' });
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  const getConfig = resolveGetConfig(getConfigProp);

  useEffect(() => { fetchEntries(); /* eslint-disable-next-line */ }, [sportSlug]);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API_URL}/result-entries/me?sport=${encodeURIComponent(sportSlug)}`, getConfig());
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching results:', err);
    }
  };

  const showMsg = (text, type = '') => {
    setMessage(text); setMessageType(type);
    setTimeout(() => setMessage(''), type === 'error' ? 4000 : 3000);
  };

  const setField = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const setCtx = (key, value) => setForm(prev => ({ ...prev, context: { ...prev.context, [key]: value } }));

  const resetForm = () => { setForm({ ...EMPTY, event_key: isFlat ? 'pairs' : '' }); setEditingId(null); };

  const buildContext = () => {
    const out = {};
    for (const f of contextFields) {
      const v = (form.context[f.key] || '').trim ? String(form.context[f.key] || '').trim() : form.context[f.key];
      if (v) out[f.key] = v;
    }
    return Object.keys(out).length ? out : null;
  };

  const buildPayload = () => {
    const seasonYear = form.season_year
      ? Number(form.season_year)
      : (form.meet_date ? Number(form.meet_date.slice(0, 4)) : null);
    return {
      sport_slug: sportSlug,
      event_key: form.event_key,
      mark_value: form.mark_value.trim(),
      mark_numeric: parseMarkNumeric(form.mark_value, sportSlug),
      lower_is_better: lowerIsBetter(sportSlug),
      season_year: Number.isFinite(seasonYear) ? seasonYear : null,
      meet_name: form.meet_name.trim() || null,
      meet_date: form.meet_date || null,
      context: buildContext(),
    };
  };

  const doSave = async () => {
    const payload = buildPayload();
    try {
      if (editingId) {
        await axios.patch(`${API_URL}/result-entries/me/${editingId}`, payload, getConfig());
      } else {
        await axios.post(`${API_URL}/result-entries/me`, payload, getConfig());
      }
      resetForm();
      fetchEntries();
      showMsg(editingId ? 'Result updated' : 'Result added');
    } catch (err) {
      showMsg('Error saving result', 'error');
    }
  };

  const saveEntry = () => {
    if (!form.event_key) { showMsg('Pick an event', 'error'); return; }
    if (!form.mark_value.trim()) { showMsg('Enter a mark', 'error'); return; }
    const editingVerified = editingId && entries.find(e => e.id === editingId)?.verified;
    if (editingVerified) {
      setConfirmAction({
        title: 'Unverify Result',
        message: 'This result is currently verified. Editing it will clear the verification, and it can only be re-verified by ProZpects staff or a Partner.',
        confirmLabel: 'Continue', danger: true,
        onConfirm: () => { setConfirmAction(null); doSave(); },
        onCancel: () => setConfirmAction(null),
      });
      return;
    }
    doSave();
  };

  const startEdit = (e) => {
    setEditingId(e.id);
    setForm({
      event_key: e.event_key || (isFlat ? 'pairs' : ''),
      mark_value: e.mark_value || '',
      meet_name: e.meet_name || '',
      meet_date: e.meet_date || '',
      season_year: e.season_year || '',
      context: e.context ? { ...e.context } : {},
    });
  };

  const deleteEntry = async (id) => {
    try {
      await axios.delete(`${API_URL}/result-entries/me/${id}`, getConfig());
      if (editingId === id) resetForm();
      fetchEntries();
      showMsg('Result removed');
    } catch (err) {
      console.error('Error deleting result:', err);
    }
  };

  // Group by event (catalog order); beach renders one flat list.
  const grouped = useMemo(() => {
    if (isFlat) return [{ key: 'pairs', label: '', rows: entries }];
    const order = new Map(positions.map((p, i) => [p.slug, i]));
    const byEvent = new Map();
    for (const e of entries) {
      if (!byEvent.has(e.event_key)) byEvent.set(e.event_key, []);
      byEvent.get(e.event_key).push(e);
    }
    return [...byEvent.entries()]
      .sort((a, b) => (order.get(a[0]) ?? 999) - (order.get(b[0]) ?? 999))
      .map(([key, rows]) => ({ key, label: labelForEvent(key), rows }));
  }, [entries, positions, labelForEvent, isFlat]);

  const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear() - i);
  const markPlaceholder = {
    swimming: 'e.g., 1:05.42 or 52.30', rowing: 'e.g., 6:12.4', gymnastics: 'e.g., 9.850',
    golf: 'e.g., 72 or -4', diving: 'e.g., 345.60', beach_volleyball: 'e.g., 1st',
  }[sportSlug] || 'e.g., 9.85';

  return (
    <>
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">
          {message}
        </div>
      )}

      <div className="settings-section">
        <h2 className="settings-section__title">Results</h2>
        <p className="settings-section__desc">
          Log a performance for each event — one row per meet. Your best-ever (PR) and
          best-in-season (SB) results are flagged automatically.
        </p>

        <div className="form-row">
          {!isFlat && (
            <div className="form-field">
              <label className="form-field__label">Event</label>
              <select
                className="form-field__select"
                value={form.event_key}
                onChange={(e) => setField({ event_key: e.target.value })}
                data-testid="result_entries_event"
              >
                <option value="">Select event…</option>
                {groupedPositions.map(g => (
                  <optgroup key={g.group || 'events'} label={g.group || 'Events'}>
                    {g.positions.map(p => <option key={p.slug} value={p.slug}>{p.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
          )}
          <div className="form-field">
            <label className="form-field__label">Mark</label>
            <input
              type="text"
              className="form-field__input"
              value={form.mark_value}
              onChange={(e) => setField({ mark_value: e.target.value })}
              placeholder={markPlaceholder}
              data-testid="result_entries_value"
            />
          </div>
        </div>

        {contextFields.length > 0 && (
          <div className="form-row">
            {contextFields.map(f => (
              <div className="form-field" key={f.key} style={{ maxWidth: f.type === 'text' ? undefined : 200 }}>
                <label className="form-field__label">{f.label}</label>
                {f.type === 'select' ? (
                  <select
                    className="form-field__select"
                    value={form.context[f.key] || ''}
                    onChange={(e) => setCtx(f.key, e.target.value)}
                    data-testid={`result_entries_${f.key}`}
                  >
                    <option value="">{f.label}…</option>
                    {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-field__input"
                    value={form.context[f.key] || ''}
                    onChange={(e) => setCtx(f.key, e.target.value)}
                    placeholder={`${f.label}…`}
                    data-testid={`result_entries_${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label">Meet</label>
            <input
              type="text"
              className="form-field__input"
              value={form.meet_name}
              onChange={(e) => setField({ meet_name: e.target.value })}
              placeholder="e.g., State Championships"
            />
          </div>
          <div className="form-field" style={{ maxWidth: 170 }}>
            <label className="form-field__label">Date</label>
            <input
              type="date"
              className="form-field__input"
              value={form.meet_date}
              onChange={(e) => setField({ meet_date: e.target.value })}
            />
          </div>
          <div className="form-field" style={{ maxWidth: 120 }}>
            <label className="form-field__label">Season</label>
            <select
              className="form-field__select"
              value={form.season_year}
              onChange={(e) => setField({ season_year: e.target.value })}
            >
              <option value="">Year…</option>
              {yearOptions.map(yr => <option key={yr} value={yr}>{yr}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="button" variant="accent" size="sm" onClick={saveEntry} data-testid="result_entries_save">
            {editingId ? 'Save Changes' : 'Add Result'}
          </Button>
          {editingId && <Button type="button" variant="ghost" size="sm" onClick={resetForm}>Cancel</Button>}
        </div>

        {entries.length === 0 && (
          <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: 12 }}>No results logged yet.</p>
        )}
        {grouped.map(g => (
          <div key={g.key} className="ae-mark-group" style={{ marginTop: 14 }}>
            {g.label && <div className="ae-mark-group__head">{g.label}</div>}
            {g.rows.map(e => {
              const ctx = contextLabel(e, sportSlug);
              return (
                <div key={e.id} className="accolade-row">
                  <div className="accolade-row__body">
                    <span className="accolade-row__title">
                      {e.mark_value}
                      {e.is_pr && <span className="ae-mark-badge ae-mark-badge--pr">PR</span>}
                      {e.is_sb && <span className="ae-mark-badge ae-mark-badge--sb">SB</span>}
                      {e.verified && <VerifiedFlame />}
                    </span>
                    <span className="accolade-row__meta">
                      {e.meet_name && <span className="accolade-row__src">{e.meet_name}</span>}
                      {e.meet_date && <span className="accolade-row__src">{e.meet_date}</span>}
                      {ctx && <span className="accolade-row__src">{ctx}</span>}
                    </span>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(e)}>Edit</Button>
                  <Button type="button" variant="danger" size="sm" onClick={() => deleteEntry(e.id)}>Delete</Button>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          danger={confirmAction.danger}
          onConfirm={confirmAction.onConfirm}
          onCancel={confirmAction.onCancel || (() => setConfirmAction(null))}
        />
      )}
    </>
  );
}
